import React, { useState, useEffect, useCallback } from 'react';
import { Link } from "react-router-dom";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserAlt, faPen, faTrash, faCheck, faTimes, faChevronLeft } from '@fortawesome/free-solid-svg-icons';

import { updateUser } from '../../../js/SaveFunctions';
import { getData, createUniqueObjectID } from '../../../js/Helpers';
import ballsLoader from '../../../images/balls.gif';

export default function Users({ redirectURL, previousLoadScreen, schoolInfo, users, setUsers, setLoadScreen }) {
	const [isLoading, setIsLoading] = useState(true);

	const [adminMessage, setAdminMessage] = useState(null);
	const [supervisorMessage, setSupervisorMessage] = useState(null);
	const [counselorMessage, setCounselorMessage] = useState(null);

	const getUserData = useCallback(() => {
		getData('users', '/admin/get-users', {school_id: schoolInfo.school_id, schedule_version_id: schoolInfo.current_schedule_version_id}, true).then(nextUsers => {
			setUsers([...nextUsers]);
			setIsLoading(false);
		});
	}, [schoolInfo.school_id, schoolInfo.current_schedule_version_id, setUsers]);

	const clearRoleMessages = () => {
		setAdminMessage(null);
		setSupervisorMessage(null);
		setCounselorMessage(null);
	};

	const setRoleMessage = (role_id, message) => {
		if(role_id === '2')
		{
			setAdminMessage(message);
		}
		else if(role_id === '3')
		{
			setSupervisorMessage(message);
		}
		else if(role_id === '4')
		{
			setCounselorMessage(message);
		}
	};

	const sendToUpdateUser = (data, options = {}) => {
		const { showRoleMessage = true, refreshOnSuccess = true } = options;
		const payload = { ...data, school_id: schoolInfo.school_id };

		if(showRoleMessage) clearRoleMessages();

		return updateUser(payload).catch(() => {
			return {status: 'error', message: 'Something went wrong. Please try again.'};
		}).then(response => {
			const safeResponse = response || {status: 'error', message: 'Something went wrong. Please try again.'};
			const status = safeResponse.status;
			const message = safeResponse.message;
			const color = (status === 'error') ? 'red-text' : 'green-text';

			if(showRoleMessage)
			{
				setRoleMessage(payload.role_id, {class: color, message: message});

				setTimeout(function(){
					setRoleMessage(payload.role_id, null);
				}, 3000);
			}

			if(status === 'success' && refreshOnSuccess) getUserData();

			return safeResponse;
		});
	};

	const hideAllErrors = () => {
		users.forEach(user_info => {
			if('error' in user_info) delete user_info.error;
		});

		setUsers([...users]);
	};

	const addTempNewUser = (role_id) => {
		const new_user_id = createUniqueObjectID(7);

		users.push({
			user_id: new_user_id,
			new_user: true,
			editing: true,
			first_name: null,
			last_name: null,
			email: null,
			role_id: role_id,
			temp_changes: {first_name: '', last_name: '', email: '', verified: '0'}
		});
		setUsers([...users]);
	};

	const closeEditing = (user_id) => {
		const user_index = users.findIndex(user => user.user_id === user_id);
		const user_info = users[user_index];

		if(!user_info || user_info.isSaving) return;

		if('new_user' in user_info)
		{
			users.splice(user_index, 1);
		}
		else
		{
			delete user_info.editing;
			delete user_info.temp_changes;
			delete user_info.error;
		}

		hideAllErrors();
		setUsers([...users]);
	};

	const openEditing = (user_id) => {
		const user_index = users.findIndex(user => user.user_id === user_id);
		const user_info = users[user_index];

		if(!user_info) return;

		user_info.editing = true;
		user_info.temp_changes = {
			first_name: user_info.first_name || '',
			last_name: user_info.last_name || '',
			email: user_info.email || '',
			verified: user_info.verified || '0'
		};

		setUsers([...users]);
	};

	const updateTempChange = (user_id, user_key, value) => {
		const user_index = users.findIndex(user => user.user_id === user_id);
		const user_info = users[user_index];

		if(!user_info || !user_info.temp_changes) return;

		user_info.temp_changes[user_key] = value;
		setUsers([...users]);
	};

	const clearUserErrorAfterDelay = (user_id) => {
		setTimeout(function(){
			setUsers(prevUsers => {
				const nextUsers = [...prevUsers];
				const user_info = nextUsers.find(user => user.user_id === user_id);

				if(user_info && 'error' in user_info) delete user_info.error;

				return nextUsers;
			});
		}, 3000);
	};

	const handleUpdateUser = (e, user_id) => {
		if(e.type === 'keyup' && e.key !== 'Enter') return;

		const user_index = users.findIndex(user => user.user_id === user_id);
		const user_info = users[user_index];

		if(!user_info || !user_info.temp_changes) return;

		const update_type = ('new_user' in user_info) ? 'add' : 'update';

		if(!user_info.temp_changes.last_name || !user_info.temp_changes.first_name || !user_info.temp_changes.email)
		{
			user_info.error = 'User info is missing!';
			setUsers([...users]);
			clearUserErrorAfterDelay(user_id);
			return;
		}

		if(update_type === 'add')
		{
			user_info.isSaving = true;
			delete user_info.error;
			setUsers([...users]);

			const data = {
				update_type: 'add',
				user_id: user_info.user_id,
				role_id: user_info.role_id,
				first_name: user_info.temp_changes.first_name,
				last_name: user_info.temp_changes.last_name,
				email: user_info.temp_changes.email,
				verified: '0'
			};

			sendToUpdateUser(data, {showRoleMessage: false}).then(response => {
				if(response.status === 'error')
				{
					setUsers(prevUsers => {
						const nextUsers = [...prevUsers];
						const current_user = nextUsers.find(user => user.user_id === user_id);

						if(current_user)
						{
							delete current_user.isSaving;
							current_user.error = response.message;
						}

						return nextUsers;
					});

					clearUserErrorAfterDelay(user_id);
				}
			});

			return;
		}

		user_info.first_name = user_info.temp_changes.first_name;
		user_info.last_name = user_info.temp_changes.last_name;
		user_info.email = user_info.temp_changes.email;

		const data = {
			update_type: 'update',
			user_id: user_info.user_id,
			role_id: user_info.role_id,
			first_name: user_info.first_name,
			last_name: user_info.last_name,
			email: user_info.email,
			verified: user_info.verified
		};

		delete user_info.editing;
		delete user_info.temp_changes;
		delete user_info.error;

		setUsers([...users]);
		sendToUpdateUser(data);
	};

	const handleDeleteUser = (e, user_id, role_id) => {
		const user_index = users.findIndex(user => user.user_id === user_id);
		const user_info = users[user_index];

		if(!user_info || user_info.deleting) return;

		user_info.deleting = true;
		setUsers([...users]);

		const data = {update_type:'delete', user_id:user_id, role_id:role_id};

		sendToUpdateUser(data).then(response => {
			if(response.status === 'error')
			{
				setUsers(prevUsers => {
					const nextUsers = [...prevUsers];
					const current_user = nextUsers.find(user => user.user_id === user_id);

					if(current_user) delete current_user.deleting;

					return nextUsers;
				});
			}
		});
	};

	const resendTempPassword = (user_id, role_id) => {
		const user_index = users.findIndex(user => user.user_id === user_id);
		const user_info = users[user_index];

		if(!user_info || user_info.resendLoading) return;

		user_info.resendLoading = true;
		delete user_info.resendMessage;
		setUsers([...users]);

		const data = {update_type:'resend', user_id:user_id, role_id:role_id};

		sendToUpdateUser(data, {showRoleMessage: false, refreshOnSuccess: false}).then(response => {
			const color = (response.status === 'error') ? 'red-text' : 'green-text';
			const message = response.message;

			setUsers(prevUsers => {
				const nextUsers = [...prevUsers];
				const current_user = nextUsers.find(user => user.user_id === user_id);

				if(current_user)
				{
					delete current_user.resendLoading;
					current_user.resendMessage = {class: color, message: message};
				}

				return nextUsers;
			});

			setTimeout(function(){
				setUsers(prevUsers => {
					const nextUsers = [...prevUsers];
					const current_user = nextUsers.find(user => user.user_id === user_id);

					if(current_user && current_user.resendMessage && current_user.resendMessage.message === message)
					{
						delete current_user.resendMessage;
					}

					return nextUsers;
				});
			}, 3000);
		});
	};

	useEffect(() => {
		getUserData();
	}, [getUserData]);

	const admins = users.filter(user => user.role_id === '2');
	const supervisors = users.filter(user => user.role_id === '3');
	const counselors = users.filter(user => user.role_id === '4');

	const renderEditingRow = (user) => {
		const user_id = user.user_id;
		const has_error = ('error' in user) ? true : false;
		const isSaving = !!user.isSaving;

		return (
			<div className='users-add-new-user-row' key={user_id}>
				<div className='users-add-new-user-inner-row'>
					<div className='users-add-new-user-label'>Last Name</div>
					<div className='users-add-new-user-label'>First Name</div>
					<div className='users-add-new-user-label'>Email</div>
					<div className='white-text'>.</div>
				</div>
				{isSaving ?
					(
						<div className='users-add-new-user-loader-row'>
							<img src={ballsLoader} alt='adding user...' style={{height:'34px'}}/>
						</div>
					) :
					(
						<div className='users-add-new-user-inner-row'>
							<input
								className={`users-add-new-user-input ${has_error ? 'error' : ''}`}
								type='text'
								value={user.temp_changes.last_name}
								onChange={(e) => updateTempChange(user_id, 'last_name', e.target.value)}
								onKeyUp={(e) => handleUpdateUser(e, user_id)}
								autoFocus={true}
							/>
							<input
								className={`users-add-new-user-input ${has_error ? 'error' : ''}`}
								type='text'
								value={user.temp_changes.first_name}
								onChange={(e) => updateTempChange(user_id, 'first_name', e.target.value)}
								onKeyUp={(e) => handleUpdateUser(e, user_id)}
							/>
							<input
								className={`users-add-new-user-input ${has_error ? 'error' : ''}`}
								type='text'
								value={user.temp_changes.email}
								onChange={(e) => updateTempChange(user_id, 'email', e.target.value)}
								onKeyUp={(e) => handleUpdateUser(e, user_id)}
							/>
							<div className='users-add-new-col users-add-new-btns'>
								<FontAwesomeIcon className='users-add-new-icons users-add-new-confirm' icon={faCheck} onClick={(e) => handleUpdateUser(e, user_id)}/>
								<FontAwesomeIcon className='users-add-new-icons users-add-new-exit' icon={faTimes} onClick={() => closeEditing(user_id)}/>
							</div>
						</div>
					)
				}
				{has_error &&
					<div className='add-new-user-error-message'>{user.error}</div>
				}
			</div>
		);
	};

	const renderUserRow = (user, roleLabel, role_id) => {
		const user_id = user.user_id;

		if(user.editing)
		{
			return renderEditingRow(user);
		}

		if(user.deleting)
		{
			return (
				<div className='user-row' key={user_id}>
					<div className='user-row-loader'>
						<img src={ballsLoader} alt='deleting user...' style={{height:'30px'}}/>
					</div>
				</div>
			);
		}

		return (
			<div className='user-row' key={user_id}>
				<div className='user-name'>{user.last_name}, {user.first_name}</div>
				<div className='user-email'>{user.email}</div>
				<div className='user-account-type'>
					<div>{roleLabel} <span className={`user-verified small-text ${user.verified === '1' ? 'green-text' : 'gray-text'}`}>{user.verified === '1' ? 'Verified' : 'Not Verified'}</span></div>
					{user.verified === '0' &&
						<>
						{user.resendLoading ?
							(
								<div className='users-inline-loader'>
									<img src={ballsLoader} alt='resending password...' style={{height:'18px'}}/>
								</div>
							) :
							(
								<div className='blue-link' onClick={() => resendTempPassword(user_id, role_id)}>Resend Password</div>
							)
						}
						{user.resendMessage &&
							<div className={`users-inline-status-message ${user.resendMessage.class}`}>{user.resendMessage.message}</div>
						}
						</>
					}
				</div>
				<div className='user-options'>
					<FontAwesomeIcon className='user-edit' icon={faPen} onClick={() => openEditing(user_id)}/>
					<FontAwesomeIcon className='user-delete' icon={faTrash} onClick={(e) => handleDeleteUser(e, user_id, role_id)}/>
				</div>
			</div>
		);
	};

	const renderRoleSection = ({ headerClass, title, roleLabel, role_id, roleUsers, message }) => (
		<div className='user-accounts-container'>
			<div className={`user-account-header ${headerClass}`}>
				<FontAwesomeIcon className='user-account-header-icon white-text' icon={faUserAlt}/>
				<div className='user-account-header-label'>{title}</div>
			</div>

			<div className='user-accounts-inner-container'>
				{isLoading ?
					(
						<div className='center-text'>
							<img src={ballsLoader} alt='loading...' style={{height:'40px'}}/>
						</div>
					) :
					(
						<>
						{roleUsers.map(user => renderUserRow(user, roleLabel, role_id))}
						{message &&
							<div className={`users-status-message ${message.class}`}>{message.message}</div>
						}
						<div className='users-add-new-user-btn' onClick={() => addTempNewUser(role_id)}>Add New User</div>
						</>
					)
				}
			</div>
		</div>
	);

	return (
		<>
		{redirectURL &&
			<Link className='redirect-btn' to={redirectURL} onClick={() => setLoadScreen(previousLoadScreen)}><FontAwesomeIcon icon={faChevronLeft} className='redirect-btn-icon'/>Back to Previous Screen</Link>
		}
		<div className='page-main-heading' style={{marginTop:`${redirectURL ? '25px' : '0px'}`}}>Users</div>
		<p>Manage all your admin accounts, school counselor accounts, and department supervisor accounts</p>
		{renderRoleSection({ headerClass: 'blue', title: 'Admins', roleLabel: 'Admin', role_id: '2', roleUsers: admins, message: adminMessage })}
		{renderRoleSection({ headerClass: 'purple', title: 'Department Supervisors', roleLabel: 'Supervisor', role_id: '3', roleUsers: supervisors, message: supervisorMessage })}
		{renderRoleSection({ headerClass: 'turquoise', title: 'Counselors', roleLabel: 'Counselor', role_id: '4', roleUsers: counselors, message: counselorMessage })}
		</>
	);
}
